
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './TermsSlider.css';
import { ButtonWrapper } from '../LinkWrapper/LinkWrapper';
import { useWidthContext } from '../../providers/Width';
import { IDigitalBankTerms, IRate } from '../../types/shared/DigitalBankTerms';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { useTermRatesContext } from '../../providers/TermRates/TermRates';
import { retrieveDBOsgiUrl } from '../../utils/cob-utils';
import { InputBase } from 'component-library/index';
import { Button, ToolTip } from '../../importCL';

interface IProductInformation {
    active: boolean;
    apy: number;
    link: string;
    product: string;
    publishDate: string;
    rate: number;
    selected: boolean;
    term: number;
}


interface ITermsSliderProps extends MappedComponentProperties {
    apiCheckbox?: boolean;
    apiUrl?: string;
    toolTipMessage?: string;
    lastSelectedIndex?: string;
    buttonId?: string;
    usingDigitalBankTermsProvider?: boolean;
}

export const TermsSliderEditConfig = {
    emptyLabel: 'Terms Slider',
    isEmpty: (props: PropsWithChildren<ITermsSliderProps>) => {
        return !props || !props.toolTipMessage;
    },
};

const TermsSlider = (props: PropsWithChildren<ITermsSliderProps>): JSX.Element => {
    const { termRates } = useTermRatesContext();
    const [terms, setTerms] = useState<IProductInformation[]>();
    const [term, setTerm] = useState({
        selectedTerm: 0,
        apy: 0,
        rate: 0,
        redirectLink: '',
    });
    const [index, setIndex] = useState<string>(props.lastSelectedIndex ?? '0');
    const [deposit, setDeposit] = useState<string>('');
    const [error, setError] = useState<boolean>(false);
    const [earningPotential, setEarningPotential] = useState<string>();
    const baseEarnPotential = 100000 * Math.pow(1 + (term.rate ?? 1) / 365, 365 * (term.selectedTerm / 12)) - 100000;

    const getDigitalBankTermType = (): keyof IDigitalBankTerms|null => {
        const pathname = window.location.href;
        if (pathname.includes('deposits/cd')) {
            return 'jumboCd';
        } else if (pathname.includes('deposits/bump-cd')) {
            return 'bumpUpCd';
        } else if (pathname.includes('deposits/savings')) {
            return 'highYieldSavings';
        }

        return null;
    };

    useEffect(() => {
        let termsMetadata : IDigitalBankTerms;

        if (!props.usingDigitalBankTermsProvider && Object.keys(termRates).length !== 0) {
            termsMetadata = termRates;
        } else if (props.usingDigitalBankTermsProvider) {
            termsMetadata = JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') as string);
        } else {
            return;
        }

        const termsKey = getDigitalBankTermType();

        if (!termsKey) {
            return;
        }
        
        const terms = (termsMetadata[termsKey] as IRate[])
            .sort((a, b) => parseInt(a?.term ?? '') - parseInt(b?.term ?? ''))
            .map((rate, i) => ({
                term: parseInt(rate?.term ?? ''),
                apy: Number(rate?.apy ?? 0),
                rate: Number(rate?.apr ?? 0),
                link: rate?.url ?? '',
                product: 'standard',
                active: true,
                selected: i === 0,
                publishDate: '',
            }));

        setTerms(terms);
    }, [termRates]);

    useEffect(() => {
        if (terms !== undefined && terms.length > 0) {
            const currentProduct = terms[parseInt(index)];
            const stateProduct = {
                selectedTerm: currentProduct.term,
                apy: currentProduct.apy,
                rate: currentProduct.rate/100,
                redirectLink: retrieveDBOsgiUrl(currentProduct.link),
            };

            setTerm(stateProduct);
        }
    }, [index, terms]);

    const calculateEarnPotential = () => {
        const earnPotential = parseInt(getDeposit()) * Math.pow(1 + (term.rate ?? 1) / 365, 365 * (term.selectedTerm / 12)) - parseInt(getDeposit());
        setEarningPotential('$' + earnPotential.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,'));
    };

    const getDeposit = () => {
        return deposit.replace(',', '')
            .replace('$', '')
            .replace(/^0/g, '')
            .replace(/[^0-9. ]+/g, '');
    };

    const handleDeposit = () => {
        if (deposit.length > 0) {
            const input = getDeposit();
            if (input !== '') {
                return '$' + parseInt(input).toLocaleString('en-US');
            }
            
            return '$';    
        }
        
        return '';
        
    };

    const checkProperAmount = () => {
        const input = getDeposit();

        if (parseInt(input) >= 100000 && parseInt(input) <= 1000000) {
            return '--correct';
        }
        
        return '';
        
    };

    const getDropdown = () => {
        return <select defaultValue={parseInt(index)} className='TermsSlider__monthSelector' onChange={(e) => {
            setIndex(e.target.value); 
        }}>
            {terms && terms.map((product, productIndex) => {
                return <option key={productIndex} value={productIndex}>{product.term + ' Mo.'}</option>;
            })}
        </select>;
    };

    const makeSlider = () => {
        if (terms !== undefined) {
            return  terms.map((product, productIndex) => {
                return <div key={productIndex}>
                    <div>
                        <div className={'TermsSlider__sliderBullet' + (parseInt(index) === productIndex ? '--selected' : '')}></div>
                    </div>
                    <div className='TermsSlider__sliderBulletMonthHolder'>
                        <div className={'TermsSlider__sliderBulletMonth' + (parseInt(index) === productIndex ? '--selected' : '')}
                            aria-label={product.term + ' months at ' + product.apy}>
                            {product.term + 'Mo.'}
                        </div>
                    </div>                      
                </div>; 
            });
        }
        
        return <div></div>;
        
    };

    const desktopTermsSlider = () => {

        return <div>
            <div className='TermsSlider__inputs'>
                <div className='TermsSlider__slider'>
                    <div className='TermsSlider__sliderBarContainer'>
                        <input onChange={e => setIndex((parseInt(e.target.value) - 1).toString())}
                            value={parseInt(index) + 1 }
                            aria-valuenow={parseInt(index) + 1}
                            aria-valuemax={terms?.length}
                            role='slider'
                            aria-valuemin={1} aria-label={(terms ?? []).length > 0 ? terms![parseInt(index)].term + ' months at ' +
                                terms![parseInt(index)].apy : ''} aria-orientation='horizontal'
                            type='range'
                            className='TermsSlider__sliderBar'
                            min={1} max={terms?.length ?? 1}/>
                        {makeSlider()}
                    </div>
                </div>
                <label className='TermsSlider__prompt'>Initial Deposit</label>
                <div className='TermsSlider__calculator'>
                    <div className='TermsSlider__depositBox'>
                        <InputBase
                            onBlur= {() => checkProperAmount() === '' ? setError(true) : setError(false)}  
                            placeholder='$100,000 - $1,000,000' id='input-deposit' 
                            maxLength={10} type='tel' 
                            value={handleDeposit()} 
                            setValue={setDeposit}
                            error={error}
                            onFocus={() => setError(false)}
                            alternate
                            noErrorsOrHelper/>
                        <svg className={'TermsSlider__checkmark' + checkProperAmount()}  
                            height='1rem' width='1rem'  
                            aria-hidden='true' data-prefix='far' 
                            data-icon='check' role='img' xmlns='http://www.w3.org/2000/svg' 
                            viewBox='0 0 512 512' data-fa-i2svg=''>
                            <path fill='currentColor' 
                                d='M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z'>
                            </path>
                        </svg>
                    </div>
                    <Button className='TermsSlider__calculateButton' onClick={() => calculateEarnPotential()} 
                        variant={checkProperAmount() === '--correct' ? 'solid' : 'inactive'} 
                        disabled={checkProperAmount() === '--correct' ? false : true} size='medium'>Calculate</Button>
                </div>
                <p className={'TermsSlider__enterMessage' + (error ? '--error' : '')}> Please enter an amount between $100,000 - $1,000,000</p>
                <div className='TermsSlider__prompt'> Your Interest Earning Potential {props.toolTipMessage && <ToolTip className='TermsSlider__toolTip' fill='#333' notInline={false} mobileNotchPosition='top-right' notchPosition='top-left'>{transformHTMLParserLinks(props.toolTipMessage)}</ToolTip>}</div>
                <h3 className='TermsSlider__earnPotential'>{earningPotential ?? '$' + baseEarnPotential.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}</h3>
                <ButtonWrapper className='TermsSlider__openAccountButton' size='medium' href={term.redirectLink} id={props.buttonId}>Open Your Account</ButtonWrapper>
            </div>
        </div>;
    };
    
    const mobileTermsDropdown = () => {
        return <div>
            <div className='TermsSlider__inputs'>
                <div className='TermsSlider__prompt'> Choose Term Length</div>
                <div className='TermsSlider__dropdown'>
                    {getDropdown()}
                    <svg className='TermsSlider__downArrow' fill='#ababab' 
                        height='0.7rem' width='0.7rem' version='1.1' id='Layer_1' 
                        xmlns='http://www.w3.org/2000/svg' viewBox='0 0 407.437 407.437' 
                        stroke='#ababab' strokeWidth='60'>
                        <g id='SVGRepo_bgCarrier' strokeWidth='60'></g>  
                        <g id='SVGRepo_iconCarrier'>  
                            <polygon strokeWidth='60' points='386.258,91.567 203.718,273.512 21.179,91.567 0,112.815 203.718,315.87 407.437,112.815 '></polygon>  
                        </g>
                    </svg>
                </div>
                <label className='TermsSlider__prompt'>Initial Deposit</label>
                <div className='TermsSlider__depositBox'>
                    <InputBase
                        onBlur= {() => checkProperAmount() === '' ? setError(true) : setError(false)}  
                        placeholder='$100,000 - $1,000,000' id='input-deposit' 
                        maxLength={10} type='tel'
                        value={handleDeposit()} 
                        setValue={setDeposit}
                        error={error}
                        onFocus={() => setError(false)}
                        alternate
                        noErrorsOrHelper/>
                    <svg className={'TermsSlider__checkmark' + checkProperAmount()}  
                        height='1rem' width='1rem'  aria-hidden='true' data-prefix='far' 
                        data-icon='check' role='img' xmlns='http://www.w3.org/2000/svg' 
                        viewBox='0 0 512 512' data-fa-i2svg=''>
                        <path fill='currentColor' 
                            d='M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z'>
                        </path>  
                    </svg>
                </div>
                <p className={'TermsSlider__enterMessage' + (error ? '--error' : '')}> Please enter an amount between $100,000 - $1,000,000</p>
                <Button className='TermsSlider__calculateButton' onClick={() => calculateEarnPotential()} 
                    variant={checkProperAmount() === '--correct' ? 'solid' : 'inactive' } 
                    disabled={checkProperAmount() === '--correct' ? false : true} size='medium'>Calculate
                </Button>
                <div className='TermsSlider__prompt'> Your Interest Earning Potential {props.toolTipMessage && <ToolTip className='TermsSlider__toolTip' fill='#333' notInline={false} mobileNotchPosition='top-right' notchPosition='top-left' >{transformHTMLParserLinks(props.toolTipMessage)}</ToolTip>}</div>
                <h3 className='TermsSlider__earnPotential'>{earningPotential ?? '$' + baseEarnPotential.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}</h3>
                <ButtonWrapper className='TermsSlider__openAccountButton' size='medium' href={term.redirectLink} id={props.buttonId}>Open Your Account</ButtonWrapper>
            </div>
        </div>;
    };
    
    const { width } = useWidthContext();

    return (<div id='terms-slider' className='TermsSlider'>
        <div className='TermsSlider__banner'>
            { terms && <div className='TermsSlider__APY'>{term.apy.toFixed(2)}</div>}
            <div className='TermsSlider__percentage'> 
                <div className='TermsSlider__percentSign'>%</div>
                <div>APY</div>
            </div>
            { terms && <div className='TermsSlider__term'>{term.selectedTerm} Mo.</div>}
        </div>
        {width >= 768 ? desktopTermsSlider() : mobileTermsDropdown()}
    </div>);
};

export default TermsSlider;
